import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Crown, FileText, Info, Loader2, Settings, X, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHasFeature } from "@/lib/useHasFeature";
import { resolveProcessingMode } from "@/lib/upload/resolveProcessingMode";
import ProcessingModeSelector from "@/components/upload/ProcessingModeSelector";
import ClipFormatSelector from "@/components/upload/ClipFormatSelector";
import { DEFAULT_CLIP_FORMAT } from "@/lib/projects/clipFormats";
import { useNavigate } from "react-router-dom";
import { useCreateMyProjectMutation, useMyBrandKitsQuery } from "@/generated/graphql";
import { useTrackProjectCreated } from "@/hooks/use-track-project-created";
import { useToast } from "@/hooks/use-toast";
import { client } from "@/lib/apollo-client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import BrandKitScrollRow from "@/components/BrandKitScrollRow";
import { useUploadFile } from "@/hooks/use-upload-file";

interface DuplicateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  project: {
    id: string;
    name?: string | null;
    duration_seconds?: number | null;
    format: string;
    language: string;
    start_time?: number | null;
    end_time?: number | null;
    processing_mode?: string | null;
    news_pdf_mirror_url?: string | null;
  } | null;
}

const DuplicateProjectDialog = ({ isOpen, onClose, project }: DuplicateProjectDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [selectedPrompt, setSelectedPrompt] = useState("clipes-virais");
  const [customPrompt, setCustomPrompt] = useState("");
  const [selectedBrandKitId, setSelectedBrandKitId] = useState("none");
  const [selectedFormat, setSelectedFormat] = useState<string>(project?.format || DEFAULT_CLIP_FORMAT);
  const [selectedProcessingMode, setSelectedProcessingMode] = useState(project?.processing_mode || "automatic");
  const hasProFeatures = useHasFeature('pro_features');
  const { mode: effectiveProcessingMode, automaticLocked } = resolveProcessingMode({
    isPro: hasProFeatures,
    requested: selectedProcessingMode,
  });

  const { data: brandKitsData, loading: brandKitsLoading } = useMyBrandKitsQuery();
  const [createMyProject, { loading: isCreating }] = useCreateMyProjectMutation();
  const trackProjectCreated = useTrackProjectCreated("project", isOpen);

  const { getUploadUrl } = useUploadFile();
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const isPdfProject = !!project?.news_pdf_mirror_url;

  const handlePdfSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast({
        title: t("duplicateProject.invalidPdf"),
        variant: "destructive",
      });
      if (pdfInputRef.current) pdfInputRef.current.value = "";
      return;
    }
    setPdfFile(file);
    if (pdfInputRef.current) pdfInputRef.current.value = "";
  };

  useEffect(() => {
    setPdfFile(null);
    setIsUploadingPdf(false);
  }, [isOpen]);

  const promptOptions = [
    { id: "clipes-virais", label: t('uploadModal.viralClips'), description: t('uploadModal.viralClipsDesc') },
    { id: "frases-memoraveis", label: t('uploadModal.memorableQuotes'), description: t('uploadModal.memorableQuotesDesc') },
    { id: "clipes-topicos", label: t('uploadModal.topicClips'), description: t('uploadModal.topicClipsDesc') },
    { id: "anuncios-produtos", label: t('uploadModal.productAds'), description: t('uploadModal.productAdsDesc') },
    { id: "depoimento", label: t('uploadModal.testimonials'), description: t('uploadModal.testimonialsDesc') },
    { id: "outro", label: t('uploadModal.other'), description: t('uploadModal.otherDesc') },
  ];

  const handleConfirm = async () => {
    if (!project) return;

    const selectedPromptData = promptOptions.find(option => option.id === selectedPrompt);
    const prompt = selectedPrompt === "outro" ? customPrompt.trim() : selectedPromptData?.description;

    if (!prompt) return;

    try {
      let newsPdfMirror: { url: string; type: string; size: number } | undefined;
      if (pdfFile) {
        setIsUploadingPdf(true);
        try {
          const pdfUrl = await getUploadUrl(pdfFile);
          if (!pdfUrl) {
            throw new Error(t('duplicateProject.pdfUploadError'));
          }
          newsPdfMirror = { url: pdfUrl, type: pdfFile.type, size: pdfFile.size };
        } finally {
          setIsUploadingPdf(false);
        }
      }

      const result = await createMyProject({
        variables: {
          input: {
            // MyProject.name and duration_seconds are nullable but the create
            // input requires them. The backend stores '' for unnamed projects
            // and derives the real duration from the source video itself.
            name: project.name ?? '',
            duration_seconds: project.duration_seconds ?? 0,
            format: selectedFormat,
            language: project.language,
            prompt,
            processing_mode: effectiveProcessingMode,
            ...(project.start_time != null && project.end_time != null ? {
              start_time: project.start_time,
              end_time: project.end_time,
            } : {}),
            ...(selectedBrandKitId && selectedBrandKitId !== "none" ? { brand_kit_id: selectedBrandKitId } : {}),
            ...(newsPdfMirror ? { news_pdf_mirror: newsPdfMirror } : {}),
            source_project_id: project.id,
          },
        },
      });

      if (result.data?.createMyProject) {
        trackProjectCreated();

        await client.refetchQueries({ include: ['MyProjects'] });

        toast({
          title: t('duplicateProject.success'),
          description: t('duplicateProject.successDescription'),
          duration: 5000,
        });

        onClose();
        navigate('/projects');
      }
    } catch (error) {
      console.error('Failed to duplicate project:', error);
      toast({
        title: t('duplicateProject.error'),
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  const canSubmit = selectedPrompt !== "outro" || customPrompt.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>{t('duplicateProject.title')}</DialogTitle>
          <DialogDescription>
            {t('duplicateProject.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2 min-w-0">
          <ClipFormatSelector value={selectedFormat} onChange={setSelectedFormat} />

          {/* Processing Mode Selector */}
          <div className="space-y-3">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Zap className="w-4 h-4 text-primary" />
              {t('copyLibrary.processingMode')}
            </label>
            <ProcessingModeSelector
              value={effectiveProcessingMode}
              onChange={setSelectedProcessingMode}
              automaticLocked={automaticLocked}
            />
          </div>

          {/* Prompt Selector */}
          <div className="space-y-4">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Crown className="w-4 h-4 text-primary" />
              {t('uploadModalExtra.whatYouLookFor')}
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {promptOptions.map((option) => (
                <div
                  key={option.id}
                  className={`p-3 rounded-xl border-2 transition-all duration-300 relative ${
                    selectedPrompt === option.id
                      ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/5 shadow-glow cursor-pointer hover:scale-105'
                      : 'border-border hover:border-primary/40 bg-background/50 cursor-pointer hover:scale-105'
                  }`}
                  onClick={() => setSelectedPrompt(option.id)}
                >
                  <h3 className="font-semibold text-xs text-foreground flex items-center justify-between gap-2">
                    <span className="inline-block">{option.label}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-xs">{option.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </h3>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Prompt Input */}
          {selectedPrompt === "outro" && (
            <div className="space-y-3">
              <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                <Settings className="w-4 h-4 text-primary" />
                {t('uploadModalExtra.describeWhatYouLookFor')}
              </label>
              <textarea
                placeholder={t('uploadModalExtra.customPromptPlaceholder')}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                className="min-h-[100px] w-full rounded-md border border-primary/20 bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shadow-sm focus:shadow-glow focus:border-primary/40 transition-all duration-300"
                rows={4}
              />
              {customPrompt.trim() === "" && (
                <p className="text-xs text-muted-foreground">
                  {t('uploadModalExtra.customPromptHint')}
                </p>
              )}
            </div>
          )}

          {/* Brand Kit Selection */}
          <div className="space-y-3">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Crown className="w-4 h-4 text-primary" />
              {t('uploadModal.brandKit')}
            </label>
            {brandKitsLoading ? (
              <div className="h-10 border border-primary/20 rounded-md flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">{t('uploadModal.loadingBrandKits')}</span>
              </div>
            ) : (() => {
                const allBrandKits = brandKitsData?.myBrandKits || [];

                return (
                  <div className="space-y-3">
                    <div
                      className={`cursor-pointer rounded-lg border-2 transition-colors p-2 text-center text-sm ${
                        selectedBrandKitId === "none"
                          ? 'border-primary bg-primary/10'
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                      onClick={() => setSelectedBrandKitId("none")}
                    >
                      {t('uploadModal.noBrandKit')}
                    </div>
                    {allBrandKits.length > 0 && (
                      <BrandKitScrollRow
                        kits={allBrandKits}
                        selectedId={selectedBrandKitId}
                        onSelect={setSelectedBrandKitId}
                        showDefault
                      />
                    )}
                  </div>
                );
              })()
            }
          </div>

          {/* Optional Replacement PDF — only for PDF projects */}
          {isPdfProject && (
            <div className="space-y-3">
              <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                <FileText className="w-4 h-4 text-primary" />
                {t('duplicateProject.replacePdf')}
              </label>
              {pdfFile ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-green-800">{pdfFile.name}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPdfFile(null)}
                    disabled={isCreating || isUploadingPdf}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full h-20 border-dashed border-2 border-primary/40 hover:border-primary"
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={isCreating || isUploadingPdf}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-6 h-6 text-primary" />
                      <span className="text-sm">{t('duplicateProject.selectPdf')}</span>
                    </div>
                  </Button>
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfSelection}
                    style={{ display: 'none' }}
                  />
                </>
              )}
              <p className="text-xs text-muted-foreground">
                {t('duplicateProject.replacePdfHint')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreating || isUploadingPdf}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="gradient"
            onClick={handleConfirm}
            disabled={isCreating || isUploadingPdf || !canSubmit}
          >
            {isUploadingPdf ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('duplicateProject.uploadingPdf')}
              </>
            ) : isCreating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('duplicateProject.creating')}
              </>
            ) : (
              t('duplicateProject.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateProjectDialog;
